import React, {
  useMemo, useRef, useEffect
} from 'react';
import { NavItemGlobal } from 'components/molecules';
import PropTypes from 'prop-types';
import { sitesType } from 'types';
import styled, { css } from 'styled-components/macro';
import { FixedSizeList as List } from 'react-window';
import { Link } from 'react-router-dom';

/**
 * Global Navigation: Change between subwebs
 */

const sizeOfCard = 58;

const Row = ({ index, style, data }) => {
  const itemData = useMemo(() => data && data.list && data.list[index], [data, index])
  if (!itemData) return null

  return (
    <div style={style}>
      <Link to={`/${encodeURIComponent(itemData.slug)}`}>
        <NavItemGlobal
          isActive={
            itemData.slug === data.activeSubweb
            || encodeURIComponent(itemData.slug) === data.activeSubweb
          }
          key={itemData.id}
          activeColor={itemData.color}
          title={itemData.description}
          titleShort={itemData.initials}
          stretch={data.stretch}
        />
      </Link>
    </div>
  )
}

Row.propTypes = {
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.shape({}),
  data: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape({})),
    stretch: PropTypes.bool,
    activeSubweb: PropTypes.string,
  }),
}

Row.defaultProps = {
  index: 0,
  style: {},
  data: {
    list: [],
    stretch: false,
    activeSubweb: null,
  },
}

const SubwebsList = ({
  stretch,
  sites,
  maxVisible,
  activeSubweb,
}) => {
  const listHeight = useMemo(() => {
    const numberOfCards = sites.length
    return numberOfCards < maxVisible ? sizeOfCard * numberOfCards : sizeOfCard * maxVisible
  }, [sites, maxVisible])

  // scroll to active subweb
  const listRef = useRef(null)
  const scrollToItem = (index) => {
    listRef.current.scrollToItem(index);
  };

  const activeSubwebIndex = useMemo(() => {
    if (!activeSubweb) return -1
    return sites.findIndex(site => site.slug === activeSubweb)
  }, [sites, activeSubweb])

  useEffect(() => {
    if (activeSubwebIndex === -1) return;
    const scrollToIndex = activeSubwebIndex
    scrollToItem(scrollToIndex)
  }, [activeSubwebIndex, maxVisible])

  return (
    <Styles>
      <List
        height={listHeight}
        itemCount={sites.length}
        itemSize={sizeOfCard}
        width="100%"
        itemData={{ list: sites, stretch, activeSubweb }}
        className="scrollable"
        ref={listRef}
      >
        {Row}
      </List>
    </Styles>
  );
};

SubwebsList.propTypes = {
  stretch: PropTypes.bool,
  sites: sitesType,
  maxVisible: PropTypes.number,
  activeSubweb: PropTypes.string,
};

SubwebsList.defaultProps = {
  stretch: false,
  sites: [],
  maxVisible: 6,
  activeSubweb: null,
};

const Styles = styled.div(props => css`
  .scrollable {
    overflow-y: scroll;
    overflow-x: hidden;
    /* width */
    ::-webkit-scrollbar {
      width: ${props.theme.sizes.xs3};
    }

    scrollbar-width: thin;
    scrollbar-color: ${props.theme.colors.borderLighter} ${props.theme.colors.bg};

    /* Track */
    ::-webkit-scrollbar-track {
      background: ${props.theme.colors.bg};
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background-color: ${props.theme.colors.borderLighter};
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
      background-color: ${props.theme.colors.bgDarker};
    }
  }
`)

export default SubwebsList;
